import { ApolloClient, NormalizedCacheObject, useLazyQuery, useMutation } from '@apollo/client';
import { AssetFolder, AssetOwnerCategory, AssetType } from '../models/Asset';
import {
  AssetMetadataData,
  AssetMetadataVars,
  AssetsData,
  AssetsVars,
  DeleteAssetsData,
  DeleteAssetsVars
} from '../gql-types';

import ApolloClientService from '../services/ApolloClientService';
import Config from '../config';
import GraphQL from '../gql';
import { useEffect } from 'react';

let assetApolloClient: ApolloClient<NormalizedCacheObject> = null;

function useAssets(
  types: AssetType[],
  category: AssetOwnerCategory,
  initialLoadingRequired: boolean
) {
  if (!assetApolloClient) {
    assetApolloClient = ApolloClientService.create(Config.ASSET_GQL);
  }

  const [
    loadAssets,
    { loading: assetsLoading, error: assetsError, data: assetsData }
  ] = useLazyQuery<AssetsData, AssetsVars>(GraphQL.Query.ASSETS, {
    client: assetApolloClient,
    variables: {
      types,
      category
    },
    fetchPolicy: 'cache-and-network'
  });

  const [
    _getAssetMetadata,
    {
      loading: assetMetadataLoading,
      error: assetMetadataError,
      data: assetMetadataData
    }
  ] = useLazyQuery<AssetMetadataData, AssetMetadataVars>(
    GraphQL.Query.ASSET_METADATA,
    {
      client: assetApolloClient,
      fetchPolicy: 'cache-and-network'
    }
  );

  function getAssetMetadata(
    assetFolder: AssetFolder,
    assetkey: string,
    assetPath?: string
  ) {
    _getAssetMetadata({
      variables: {
        assetFolder,
        assetkey,
        assetPath
      }
    });
  }

  useEffect(() => {
    if (initialLoadingRequired) {
      loadAssets();
    }
  }, []);

  const [
    _deleteAssets,
    {
      loading: deletingAssets,
      error: deleteAssetsError,
      data: deleteAssetsData
    }
  ] = useMutation<DeleteAssetsData, DeleteAssetsVars>(
    GraphQL.Mutation.DELETE_ASSETS,
    {
      client: assetApolloClient
    }
  );

  function deleteAssets(assetkeys: string[], category: AssetOwnerCategory) {
    _deleteAssets({ variables: { slugs: assetkeys, category } });
  }

  return {
    assetsLoading,
    assetsError,
    assetsData,
    loadAssets,
    assetMetadataLoading,
    assetMetadataError,
    assetMetadata: assetMetadataData?.assetMetadata,
    getAssetMetadata,
    deleteAssets,
    deletingAssets,
    deleteAssetsError,
    deleted: deleteAssetsData?.deleteAssets || []
  };
}

export default useAssets;
